import React, { memo, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import CreateIcon from "@mui/icons-material/Create";

import { palette } from "../../theme/palette";
import {
  SECTION_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING,
  DEFAULT_SECTION_CANNOT_BE_REMOVED,
  SECTION_WITH_ALLOCATED_DRIVERS_CANNOT_BE_REMOVED,
} from "../../consts/strings";

import { addNotification } from "../../reducers/ownApp";
import { onSectionClick } from "../../reducers/ownApp/thunkActions";

import { editSection } from "../../reducers/sectionManagement/thunkActions";

import { BinIcon } from "../Icons";
import { View } from "../View";
import { RemoveSectionModal } from "../Modals/RemoveSection/RemoveSectionModal";
import { SectionLabel } from "./SectionLabel";
import { SectionCardContent } from "./SectionCardContent";

const useStyles = makeStyles(() => {
  return {
    section: {
      display: "flex",
      flexDirection: "column",
      background: palette.typography.white.neutral,
      padding: "0.625rem",
      paddingBottom: "0.5rem",
      cursor: "default",
      height: (props) => (props.height || 1) - 1,
    },
    highlighted: {
      background: palette.typography.white.disabled,
    },
    editIconBase: {
      backgroundColor: (props) =>
        props.editing
          ? palette.orange
          : props.notInWave
          ? "#30303044"
          : "#303030",
      borderRadius: "50%",
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    editIcon: {
      color: palette.typography.white.solid,
      width: "13px",
      height: "13px",
      cursor: "pointer",
    },
    binIcon: {
      marginTop: "10px",
      fill: palette.typography.white.solid,
      height: "20px",
      width: "20px",
      cursor: "pointer",
    },
    top: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      gap: "0.3rem",
    },
    bottom: {
      display: "flex",
      flexDirection: "row",
      gap: "0.3rem",
    },
    leftButtons: {
      width: "2.5rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginRight: "auto",
    },
    right: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginLeft: "0.5rem",
      color: "white",
      lineHeight: "1.25rem",
      gap: "0.25rem",
    },
    defaultSectionLabel: {
      height: "1.25rem",
      backgroundColor: palette.orange,
      borderRadius: "1rem",
      marginRight: "0.75rem",
      padding: "0rem 1rem",
    },
  };
});

export const SectionCardBase = ({
  height,
  section,
  editMode,
  inEdit,
  selected,
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const dispatch = useDispatch();
  const styles = useStyles({ editing: inEdit, height });

  const handleClick = (e) => {
    e.preventDefault();
    if (editMode)
      dispatch(
        addNotification({
          message: SECTION_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(section),
          options: { variant: "info" },
        })
      );
    else dispatch(onSectionClick(section));
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (editMode && !inEdit)
      dispatch(
        addNotification({
          message: SECTION_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(section),
          options: { variant: "info" },
        })
      );
    else if (!editMode) dispatch(editSection(section));
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (section.defaultSection)
      dispatch(
        addNotification({
          message: DEFAULT_SECTION_CANNOT_BE_REMOVED,
          options: { variant: "info" },
        })
      );
    else if (
      [0, 1, 2, 3, 4, 5, 6].some(
        (dayOfWeek) => section.days[dayOfWeek].drivers.length
      )
    )
      dispatch(
        addNotification({
          message: SECTION_WITH_ALLOCATED_DRIVERS_CANNOT_BE_REMOVED,
          options: { variant: "info" },
        })
      );
    else setShowRemoveModal(true);
  };

  return (
    <div style={{ height }}>
      <View
        className={clsx(
          styles.section,
          (inEdit || selected) && styles.highlighted
        )}
        onClick={handleClick}
      >
        <View className={styles.top}>
          <SectionLabel section={section} />
          {section.defaultSection && (
            <View className={styles.defaultSectionLabel}>Default</View>
          )}
        </View>
        <View className={styles.bottom}>
          <View className={styles.leftButtons}>
            <View className={styles.editIconBase} onClick={handleEdit}>
              <CreateIcon fontSize={"small"} className={styles.editIcon} />
            </View>
            <BinIcon className={styles.binIcon} onClick={handleClear} />
          </View>

          <SectionCardContent section={section} />
        </View>
      </View>
      {showRemoveModal && (
        <RemoveSectionModal
          section={section}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </div>
  );
};

export default memo(SectionCardBase);
